import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { setToken } from '../lib/auth';

type Mode = 'login' | 'register';

/** 加载 Google 登录脚本并渲染按钮 */
function useGoogleLogin(
  onSuccess: (token: string) => void,
  onError: (msg: string) => void
) {
  useEffect(() => {
    let cancelled = false;

    api<{ clientId: string | null; enabled: boolean }>('/auth/google/config').then((res) => {
      if (cancelled || !res.ok || !res.data?.enabled || !res.data.clientId) return;

      const clientId = res.data.clientId;
      const existing = document.getElementById('gsi-script');

      if (existing) {
        render();
        return;
      }

      const script = document.createElement('script');
      script.id = 'gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = render;
      document.body.appendChild(script);

      function render() {
        const g = (window as unknown as { google?: any }).google;
        if (!g) return;

        g.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp: { credential?: string }) => {
            if (!resp?.credential) return;
            const r = await api<{ token: string }>('/auth/google', {
              method: 'POST',
              body: JSON.stringify({ idToken: resp.credential }),
            });
            if (r.ok && r.data) {
              onSuccess(r.data.token);
            } else {
              onError(errMsg(r, 'Google 登录失败'));
            }
          },
        });

        const el = document.getElementById('gsi-btn');
        if (el) {
          g.accounts.id.renderButton(el, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            locale: 'zh_CN',
            text: 'continue_with',
          });
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [onSuccess, onError]);
}

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneDevCode, setPhoneDevCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);

  const handleGoogleSuccess = useCallback(
    (token: string) => {
      setToken(token);
      navigate('/memorials');
    },
    [navigate]
  );

  useGoogleLogin(handleGoogleSuccess, setError);

  /** 发送手机验证码（注册场景，无需登录） */
  async function sendPhoneCode() {
    setError('');
    setSendingCode(true);

    const res = await api<{ devCode?: string }>('/phone/send', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });

    setSendingCode(false);

    if (res.ok) {
      if (res.data?.devCode) setPhoneDevCode(res.data.devCode);
    } else {
      setError(errMsg(res, '验证码发送失败'));
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'register') {
        const res = await api<{ token: string; devCode?: string }>('/auth/register', {
          method: 'POST',
          body: JSON.stringify({
            email,
            password,
            displayName,
            phone: phone || undefined,
            phoneCode: phoneCode || undefined,
          }),
        });

        if (res.ok && res.data) {
          setToken(res.data.token);
          // 未配置邮件服务时后端返回验证码，本地可直接看到
          if (res.data.devCode) {
            setDevCode(res.data.devCode);
          } else {
            navigate('/memorials');
          }
        } else {
          setError(errMsg(res));
        }
      } else {
        const res = await api<{ token: string; user: { role: string } }>('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });

        if (res.ok && res.data) {
          setToken(res.data.token);
          // 管理员直接进后台，普通用户进「我的纪念馆」
          navigate(res.data.user?.role === 'admin' ? '/admin' : '/memorials');
        } else {
          setError(errMsg(res));
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-ink-100 to-ink-50 px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-ink-900 text-lg text-candle-300">
            墓
          </div>
          <h1 className="font-serifcn text-2xl text-ink-900">
            {mode === 'login' ? '登录' : '创建账号'}
          </h1>
          <p className="mt-2 text-sm text-ink-500">
            {mode === 'login' ? '欢迎回来，继续守护这份思念' : '建立账号，为亲人创建纪念馆'}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-ink-200 bg-white/80 p-6 shadow-sm backdrop-blur"
        >
          {devCode && (
            <div className="mb-5 rounded-xl border border-candle-400/40 bg-candle-300/15 p-4 text-center">
              <p className="text-xs text-ink-600">开发环境验证码（未配置邮件服务）</p>
              <p className="mt-1 font-mono text-2xl tracking-widest text-candle-600">{devCode}</p>
              <button
                type="button"
                onClick={() => navigate('/memorials')}
                className="mt-3 text-xs text-ink-500 underline"
              >
                跳过验证，直接进入
              </button>
            </div>
          )}

          {mode === 'register' && (
            <Field
              label="昵称"
              value={displayName}
              onChange={setDisplayName}
              placeholder="家人称呼你什么？"
            />
          )}

          <Field
            label="邮箱"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
          />

          <Field
            label="密码"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={mode === 'register' ? '至少 8 位' : '请输入密码'}
          />

          {mode === 'register' && (
            <>
              <div className="mb-4">
                <label className="mb-1.5 block text-xs text-ink-600">
                  手机号（选填，含国家码）
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+14155552671"
                    className="h-11 flex-1 rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={sendPhoneCode}
                    disabled={sendingCode || !phone}
                    className="h-11 shrink-0 rounded-xl border border-ink-300 px-4 text-xs text-ink-700 transition hover:bg-ink-100 disabled:opacity-40"
                  >
                    {sendingCode ? '发送中…' : '获取验证码'}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-ink-400">
                  美国 +1、香港 +852、新加坡 +65、日本 +81
                </p>
              </div>

              {phone && (
                <Field
                  label="手机验证码"
                  value={phoneCode}
                  onChange={setPhoneCode}
                  placeholder="6 位数字"
                />
              )}

              {phoneDevCode && (
                <p className="mb-4 rounded-lg bg-candle-300/20 px-3 py-2 text-xs text-ink-700">
                  开发环境验证码：<span className="font-mono">{phoneDevCode}</span>
                </p>
              )}
            </>
          )}

          {error && (
            <p className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-xl bg-ink-900 text-sm font-medium text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
          >
            {loading ? '处理中…' : mode === 'login' ? '登录' : '注册'}
          </button>

          {mode === 'login' && (
            <div className="mt-3 text-center">
              <Link to="/forgot-password" className="text-xs text-ink-500 underline">
                忘记密码？用密保问题找回
              </Link>
            </div>
          )}

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-ink-200" />
            <span className="text-xs text-ink-400">或</span>
            <span className="h-px flex-1 bg-ink-200" />
          </div>

          <div id="gsi-btn" className="flex justify-center" />

          <p className="mt-5 text-center text-sm text-ink-500">
            {mode === 'login' ? '还没有账号？' : '已有账号？'}
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'login' ? 'register' : 'login');
                setError('');
              }}
              className="ml-1 text-ink-900 underline"
            >
              {mode === 'login' ? '立即注册' : '去登录'}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-xs text-ink-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none transition focus:border-ink-500 focus:bg-white"
      />
    </div>
  );
}
